<template>
  <BaseModal
    :show="isVisible"
    :title="`${t('app.views.cashRegister.sessionDetails')} ${session?.id ? `#${session.id}` : ''}`"
    class="print-content"
    @close="closeModal"
  >
    <!-- Loading state -->
    <LoadingSpinner
      v-if="isLoading"
      size="md"
      color="primary"
      :text="t('app.status.loading')"
    />

    <!-- Session details content -->
    <div
      v-else-if="session"
      class="space-y-4"
    >
      <SessionDetailsOverview
        :session="session"
        :session-summary="sessionSummary"
        :get-status-badge-class="getStatusBadgeClass"
      />

      <SessionDetailsSummary
        :session="session"
        :session-summary="sessionSummary"
        :payment-breakdown="paymentBreakdown"
        :last-cut="lastCut"
        :get-difference-class="getDifferenceClass"
        :get-difference-text-class="getDifferenceTextClass"
        :get-difference-label="getDifferenceLabel"
        :format-difference="formatDifference"
      />

      <SessionDetailsTransactions
        v-if="transactions && transactions.length > 0"
        :session="session"
        :transactions="transactions"
        :filtered-transactions="filteredTransactions"
        :paginated-transactions="paginatedTransactions"
        :total-transaction-pages="totalTransactionPages"
        :transaction-pagination="transactionPagination"
        :transaction-search-term="transactionSearchTerm"
        :transaction-type-filter="transactionTypeFilter"
        :get-transaction-type-badge-class="getTransactionTypeBadgeClass"
        :get-payment-method-badge-class="getPaymentMethodBadgeClass"
        :translate-payment-method="translatePaymentMethod"
        @update:transaction-search-term="transactionSearchTerm = $event"
        @update:transaction-type-filter="transactionTypeFilter = $event"
        @previous-page="previousTransactionPage"
        @next-page="nextTransactionPage"
      />

      <!-- Cuts Section -->
      <div
        v-if="session.cuts && session.cuts.length > 0"
        class="space-y-4"
      >
        <div class="flex items-center gap-2 mb-6">
          <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
          <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
            {{ t('app.views.cashRegister.cuts') }}
          </h3>
          <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
        </div>
        <div class="space-y-3">
          <div
            v-for="cut in session.cuts"
            :key="cut.id"
            class="bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-6"
          >
            <CutDetails :cut="cut" />
          </div>
        </div>
      </div>
    </div>

    <!-- No session state -->
    <div
      v-else
      class="flex justify-center items-center py-8"
    >
      <p class="text-gray-600 dark:text-gray-400">
        {{ t('app.views.cashRegister.sessionNotFound') }}
      </p>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import SessionDetailsOverview from '@/components/cashRegister/SessionDetailsOverview.vue'
import SessionDetailsSummary from '@/components/cashRegister/SessionDetailsSummary.vue'
import SessionDetailsTransactions from '@/components/cashRegister/SessionDetailsTransactions.vue'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import { useSessionDetails } from '@/composables/useSessionDetails'
import type { SessionDetailsModalProps, SessionDetailsModalEmits } from '@/components/cashRegister/sessionDetailsTypes'

const props = defineProps<SessionDetailsModalProps>()
const emit = defineEmits<SessionDetailsModalEmits>()

const { t } = useI18n()

const {
  isLoading,
  session,
  transactions,
  sessionSummary,
  paymentBreakdown,
  lastCut,
  transactionSearchTerm,
  transactionTypeFilter,
  transactionPagination,
  filteredTransactions,
  paginatedTransactions,
  totalTransactionPages,
  getStatusBadgeClass,
  getTransactionTypeBadgeClass,
  getPaymentMethodBadgeClass,
  translatePaymentMethod,
  formatDifference,
  getDifferenceClass,
  getDifferenceTextClass,
  getDifferenceLabel,
  loadSessionDetails,
  resetTransactionPagination,
  previousTransactionPage,
  nextTransactionPage,
  clearSessionData
} = useSessionDetails()

const closeModal = () => {
  emit('close')
}

const printReport = () => {
  if (!session.value) return

  // Use browser's print functionality
  window.print()
}

watch(
  [() => props.isVisible, () => props.sessionId],
  () => {
    if (props.isVisible && props.sessionId) {
      resetTransactionPagination()
      loadSessionDetails(props.sessionId)
    } else {
      clearSessionData()
    }
  },
  { immediate: true }
)
</script>

<style>
@media print {
  @page {
    margin: 0.5cm;
  }

  /* Simple approach - hide everything, show only print-content */
  body * {
    visibility: hidden;
  }

  .print-content,
  .print-content * {
    visibility: visible;
  }

  .fixed.inset-0.z-50,
  .flex.items-center {
    position: static !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .print-content {
    position: static !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Hide buttons, inputs, and icons */
  button,
  input,
  select,
  textarea,
  svg {
    display: none !important;
  }

  /* Force black and white for print */
  * {
    background-color: white !important;
    color: black !important;
    border-color: #000 !important;
    box-shadow: none !important;
  }

  /* Colored sections get borders */
  .bg-green-50,
  .bg-red-50,
  .bg-blue-50,
  .bg-purple-50,
  .dark\:bg-green-900\/30,
  .dark\:bg-red-900\/30,
  .dark\:bg-blue-900\/30,
  .dark\:bg-purple-900\/30 {
    background-color: white !important;
    border: 2px solid black !important;
  }

  /* Bold headings */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: bold !important;
  }

  /* Compact spacing */
  .p-6,
  .p-4,
  .px-4,
  .px-6,
  .pt-5,
  .pb-4 {
    padding: 0.25rem !important;
  }

  .mb-4,
  .mb-6 {
    margin-bottom: 0.25rem !important;
  }

  /* Remove rounded corners */
  .rounded-lg {
    border-radius: 0 !important;
  }
}
</style>
